import {
    SegmentBalanceSheetResponse,
    SegmentIncomeStatementResponse,
    SegmentReportLine,
    TrialBalance,
} from '../interfaces/accounting.interface';
import { sanitizeForSpreadsheet } from './csv';

/**
 * CSV exports for the three core financial statements. Each builder works from the SAME response
 * object the screen renders, so an export can never disagree with what the user is looking at.
 * Amounts are written as plain 2-dp numbers (no thousands separators) so spreadsheets read them
 * as numbers.
 */

export type CsvTable = { headers: string[]; rows: (string | number)[][] };

const amt = (v: number | null | undefined): string => (v ?? 0).toFixed(2);

/**
 * Lines worth showing on a statement: an account whose balance nets to zero (typically a group
 * account whose old direct postings were reclassified to a sub-account) is noise on a balance
 * sheet or income statement.
 */
export function nonZeroLines(lines: SegmentReportLine[]): SegmentReportLine[] {
    return lines.filter(l => Math.abs(l.netBalance) >= 0.005);
}

const label = (code: string | null | undefined, name: string) =>
    sanitizeForSpreadsheet(code ? `${code} — ${name}` : name);

export function trialBalanceCsv(tb: TrialBalance): CsvTable {
    const headers = ['Code', 'Account Name', 'Type', 'Opening Balance', 'Debits', 'Credits', 'Net Movement', 'Closing Balance'];
    const rows: (string | number)[][] = tb.lines.map(l => [
        sanitizeForSpreadsheet(l.accountCode),
        sanitizeForSpreadsheet(l.accountName),
        l.accountType,
        amt(l.openingBalance),
        amt(l.totalDebits),
        amt(l.totalCredits),
        amt(l.netBalance),
        amt(l.closingBalance),
    ]);
    rows.push(['', 'Period total', '', '', amt(tb.grandTotalDebits), amt(tb.grandTotalCredits), '', '']);
    rows.push(['', `Closing balances at ${tb.toDate} (debit / credit)`, '', '',
        amt(tb.closingTotalDebits), amt(tb.closingTotalCredits), '', '']);
    return { headers, rows };
}

export function balanceSheetCsv(bs: SegmentBalanceSheetResponse): CsvTable {
    const headers = ['Segment', 'Section', 'Code', 'Account Name', 'Amount'];
    const rows: (string | number)[][] = [];
    for (const b of bs.segments) {
        const seg = label(b.segmentCode, b.segmentName);
        const section = (name: string, lines: SegmentReportLine[]) =>
            nonZeroLines(lines).forEach(l => rows.push([seg, name,
                sanitizeForSpreadsheet(l.accountCode), sanitizeForSpreadsheet(l.accountName), amt(l.netBalance)]));
        section('Assets', b.assetLines);
        rows.push([seg, 'Assets', '', 'Total Assets', amt(b.totalAssets)]);
        section('Liabilities', b.liabilityLines);
        rows.push([seg, 'Liabilities', '', 'Total Liabilities', amt(b.totalLiabilities)]);
        section('Equity', b.equityLines);
        rows.push([seg, 'Equity', '', 'Retained Earnings (previous years)', amt(b.retainedEarnings)]);
        rows.push([seg, 'Equity', '', 'Current Year Earnings', amt(b.currentPeriodEarnings)]);
        rows.push([seg, 'Equity', '', 'Total Equity', amt(b.totalEquity)]);
        rows.push([seg, '', '', 'Total Liabilities & Equity', amt(b.totalLiabilitiesAndEquity)]);
    }
    rows.push(['All segments', '', '', 'Total Assets', amt(bs.totalAssets)]);
    rows.push(['All segments', '', '', 'Total Liabilities & Equity', amt(bs.totalLiabilitiesAndEquity)]);
    return { headers, rows };
}

export function incomeStatementCsv(is: SegmentIncomeStatementResponse): CsvTable {
    const headers = ['Segment', 'Section', 'Code', 'Account Name', 'Amount'];
    const rows: (string | number)[][] = [];
    for (const b of is.segments) {
        const seg = label(b.segmentCode, b.segmentName);
        nonZeroLines(b.revenueLines).forEach(l => rows.push([seg, 'Revenue',
            sanitizeForSpreadsheet(l.accountCode), sanitizeForSpreadsheet(l.accountName), amt(l.netBalance)]));
        rows.push([seg, 'Revenue', '', 'Total Revenue', amt(b.totalRevenue)]);
        nonZeroLines(b.expenseLines).forEach(l => rows.push([seg, 'Expenses',
            sanitizeForSpreadsheet(l.accountCode), sanitizeForSpreadsheet(l.accountName), amt(l.netBalance)]));
        rows.push([seg, 'Expenses', '', 'Total Expense', amt(b.totalExpense)]);
        rows.push([seg, '', '', 'Net Profit', amt(b.netProfit)]);
    }
    rows.push(['All segments', '', '', 'Total Revenue', amt(is.totalRevenue)]);
    rows.push(['All segments', '', '', 'Total Expense', amt(is.totalExpense)]);
    rows.push(['All segments', '', '', 'Net Profit', amt(is.netProfit)]);
    return { headers, rows };
}
